/**
 * CardCarousel — web port of `apps/WunderGuide/common/CardCarousel.tsx`.
 *
 * Same merry-go-round animation as RN: active card at translateX 0 / scale
 * 1, neighbours at translateX ±W*translateMultiplier, scale neighbourScale,
 * opacity neighbourOpacity. Cards beyond ±1 hidden. Auto-advances every
 * autoPlayInterval ms with transitionDuration ms tween. No pagination dots.
 *
 * Manual control via touch/pointer drag and `defaultIndex`. Pauses
 * auto-play on hover and during a drag.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
} from 'react';

export interface CardCarouselProps<T> {
  data: T[];
  renderItem: (item: T, index: number, isActive: boolean) => ReactNode;
  /** Stable per-item key. */
  keyFor: (item: T, index: number) => string | number;
  /** Default 7500. Set to 0 to disable. */
  autoPlayInterval?: number;
  /** Default 800. */
  transitionDuration?: number;
  /** Default 0.88. */
  neighbourScale?: number;
  /** Default 0.4. */
  neighbourOpacity?: number;
  /** Default 0.92. */
  translateMultiplier?: number;
  /** Default true. */
  enableLoop?: boolean;
  /** Default 0. */
  defaultIndex?: number;
  /** Stage height in px (also accepts CSS strings). */
  height?: number | string;
  className?: string;
  style?: CSSProperties;
  /** Fired when user manually advances. */
  onScrollStart?: () => void;
}

function CardCarousel<T>({
  data,
  renderItem,
  keyFor,
  autoPlayInterval = 7500,
  transitionDuration = 800,
  neighbourScale = 0.88,
  neighbourOpacity = 0.4,
  translateMultiplier = 0.92,
  enableLoop = true,
  defaultIndex = 0,
  height = 220,
  className,
  style,
  onScrollStart,
}: CardCarouselProps<T>) {
  const [index, setIndex] = useState(defaultIndex);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [stageWidth, setStageWidth] = useState(0);
  const pausedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const dragStartRef = useRef<{ x: number; t: number } | null>(null);

  const count = data.length;

  useEffect(() => {
    if (!stageRef.current) return;
    const el = stageRef.current;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) setStageWidth(e.contentRect.width);
    });
    ro.observe(el);
    setStageWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const advance = useCallback(
    (dir: 1 | -1) => {
      setIndex(prev => {
        const next = prev + dir;
        if (enableLoop) return (next + count) % count;
        return Math.max(0, Math.min(count - 1, next));
      });
    },
    [count, enableLoop],
  );

  useEffect(() => {
    if (autoPlayInterval <= 0 || count <= 1) return;
    const tick = () => {
      if (!pausedRef.current) advance(1);
    };
    timerRef.current = window.setInterval(tick, autoPlayInterval);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [autoPlayInterval, advance, count]);

  const onPointerDown = (e: React.PointerEvent) => {
    pausedRef.current = true;
    dragStartRef.current = { x: e.clientX, t: Date.now() };
    onScrollStart?.();
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const start = dragStartRef.current;
    dragStartRef.current = null;
    pausedRef.current = false;
    if (!start || !stageWidth) return;
    const dx = e.clientX - start.x;
    if (Math.abs(dx) > stageWidth * 0.12) {
      advance(dx > 0 ? -1 : 1);
    }
  };

  const relativeOffset = (i: number) => {
    if (!enableLoop) return i - index;
    let diff = i - index;
    if (diff > count / 2) diff -= count;
    if (diff < -count / 2) diff += count;
    return diff;
  };

  const stageStyle = useMemo<CSSProperties>(
    () => ({
      position: 'relative',
      width: '100%',
      height: typeof height === 'number' ? `${height}px` : height,
      overflow: 'hidden',
      touchAction: 'pan-y',
      ...style,
    }),
    [height, style],
  );

  if (count === 0) return null;

  return (
    <div
      ref={stageRef}
      className={className}
      style={stageStyle}
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => (pausedRef.current = false)}
    >
      {data.map((item, i) => {
        const offset = relativeOffset(i);
        const visible = Math.abs(offset) <= 1;
        const tx = offset * stageWidth * translateMultiplier;
        const scale = offset === 0 ? 1 : neighbourScale;
        const opacity = offset === 0 ? 1 : neighbourOpacity;
        const z = offset === 0 ? 10 : 1;
        return (
          <div
            key={keyFor(item, i)}
            aria-hidden={offset !== 0}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 'min(86%, 480px)',
              transform: `translate(-50%, -50%) translateX(${tx}px) scale(${scale})`,
              opacity: visible ? opacity : 0,
              zIndex: z,
              pointerEvents: offset === 0 ? 'auto' : 'none',
              transition: `transform ${transitionDuration}ms cubic-bezier(0.22,1,0.36,1), opacity ${transitionDuration}ms ease`,
              willChange: 'transform, opacity',
            }}
          >
            {renderItem(item, i, offset === 0)}
          </div>
        );
      })}
    </div>
  );
}

export default CardCarousel;
